// @flow
import { userProfile } from 'redux/User/selectors';

// Session timeout configuration (CWE-613 compliance)
const SESSION_TIMEOUT_MINUTES = 20;
const WARNING_MINUTES = 5; // Show warning 5 minutes before timeout
const CHECK_INTERVAL_SECONDS = 30; // Check activity every 30 seconds

// Session timeout utility class for CWE-613 compliance
class SessionTimeoutManager {
  constructor() {
    this.lastActivityTime = Date.now();
    this.timeoutWarningShown = false;
    this.sessionTimeoutTimer = null;
    this.warningTimeoutTimer = null;
    this.activityCheckTimer = null;
    this.onTimeoutCallback = null;
    this.onWarningCallback = null;

    // Bind methods to maintain context
    this.resetActivity = this.resetActivity.bind(this);
    this.checkActivity = this.checkActivity.bind(this);
    this.startMonitoring = this.startMonitoring.bind(this);
    this.stopMonitoring = this.stopMonitoring.bind(this);
  }

  // Initialize session timeout monitoring
  startMonitoring(onTimeoutCallback, onWarningCallback = null) {
    this.onTimeoutCallback = onTimeoutCallback;
    this.onWarningCallback = onWarningCallback;

    // Reset activity time
    this.resetActivity();

    // Set up activity listeners
    this.setupActivityListeners();

    // Start periodic activity checking
    this.activityCheckTimer = setInterval(this.checkActivity, CHECK_INTERVAL_SECONDS * 1000);
  }

  // Stop session timeout monitoring
  stopMonitoring() {
    // Clear all timers
    if (this.sessionTimeoutTimer) {
      clearTimeout(this.sessionTimeoutTimer);
      this.sessionTimeoutTimer = null;
    }
    if (this.warningTimeoutTimer) {
      clearTimeout(this.warningTimeoutTimer);
      this.warningTimeoutTimer = null;
    }
    if (this.activityCheckTimer) {
      clearInterval(this.activityCheckTimer);
      this.activityCheckTimer = null;
    }

    // Remove activity listeners
    this.removeActivityListeners();
  }

  // Reset activity timer (called on user interaction)
  resetActivity() {
    this.lastActivityTime = Date.now();
    this.timeoutWarningShown = false;

    // Clear existing timers and set new ones
    if (this.sessionTimeoutTimer) {
      clearTimeout(this.sessionTimeoutTimer);
    }
    if (this.warningTimeoutTimer) {
      clearTimeout(this.warningTimeoutTimer);
    }

    // Set warning timer (15 minutes = SESSION_TIMEOUT_MINUTES - WARNING_MINUTES)
    const warningTime = (SESSION_TIMEOUT_MINUTES - WARNING_MINUTES) * 60 * 1000;
    this.warningTimeoutTimer = setTimeout(() => {
      this.showTimeoutWarning();
    }, warningTime);

    // Set session timeout timer (20 minutes)
    const sessionTimeoutTime = SESSION_TIMEOUT_MINUTES * 60 * 1000;
    this.sessionTimeoutTimer = setTimeout(() => {
      this.handleSessionTimeout();
    }, sessionTimeoutTime);
  }

  // Check if user has been inactive for too long
  checkActivity() {
    const now = Date.now();
    const inactiveTime = now - this.lastActivityTime;
    const timeoutTime = SESSION_TIMEOUT_MINUTES * 60 * 1000;
    const warningTime = (SESSION_TIMEOUT_MINUTES - WARNING_MINUTES) * 60 * 1000;

    if (inactiveTime >= timeoutTime) {
      this.handleSessionTimeout();
    } else if (inactiveTime >= warningTime && !this.timeoutWarningShown) {
      this.showTimeoutWarning();
    }
  }

  // Show timeout warning to user
  showTimeoutWarning() {
    if (this.timeoutWarningShown) return;

    this.timeoutWarningShown = true;
    const remainingMinutes = WARNING_MINUTES;

    if (this.onWarningCallback) {
      this.onWarningCallback(remainingMinutes);
    } else {
      // Default warning behavior
      const continueSession = window.confirm(
        `Your session will expire in ${remainingMinutes} minutes due to inactivity. ` + 'Click OK to continue your session or Cancel to logout now.',
      );

      if (continueSession) {
        this.resetActivity();
      } else {
        this.handleSessionTimeout();
      }
    }
  }

  // Handle session timeout - force logout
  handleSessionTimeout() {
    // Clear all timers
    this.stopMonitoring();

    // Call logout callback
    if (this.onTimeoutCallback) {
      this.onTimeoutCallback();
    } else {
      // Default timeout behavior - redirect to login
      alert('Your session has expired due to inactivity. You will be redirected to the login page.');
      window.location.href = '/login';
    }
  }

  // Set up event listeners for user activity
  setupActivityListeners() {
    // Mouse and keyboard activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach((event) => {
      document.addEventListener(event, this.resetActivity, true);
    });

    // Window focus events
    window.addEventListener('focus', this.resetActivity);
    window.addEventListener('blur', this.resetActivity);
  }

  // Remove activity event listeners
  removeActivityListeners() {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach((event) => {
      document.removeEventListener(event, this.resetActivity, true);
    });

    window.removeEventListener('focus', this.resetActivity);
    window.removeEventListener('blur', this.resetActivity);
  }

  // Get remaining session time in milliseconds
  getRemainingTime() {
    const now = Date.now();
    const inactiveTime = now - this.lastActivityTime;
    const timeoutTime = SESSION_TIMEOUT_MINUTES * 60 * 1000;
    return Math.max(0, timeoutTime - inactiveTime);
  }

  // Check if session is still valid
  isSessionValid() {
    const hasValidCookie = !!userProfile();
    const notTimedOut = this.getRemainingTime() > 0;
    return hasValidCookie && notTimedOut;
  }

  // Extend session (can be called manually)
  extendSession() {
    this.resetActivity();
  }
}

// Create singleton instance
const sessionTimeoutManager = new SessionTimeoutManager();

// Utility functions for easy use
export const startSessionTimeout = (onTimeoutCallback, onWarningCallback = null) => {
  sessionTimeoutManager.startMonitoring(onTimeoutCallback, onWarningCallback);
};

export const stopSessionTimeout = () => {
  sessionTimeoutManager.stopMonitoring();
};

export const resetSessionActivity = () => {
  sessionTimeoutManager.resetActivity();
};

export const isSessionValid = () => {
  return sessionTimeoutManager.isSessionValid();
};

export const getRemainingSessionTime = () => {
  return sessionTimeoutManager.getRemainingTime();
};

export const extendSession = () => {
  sessionTimeoutManager.extendSession();
};

export default sessionTimeoutManager;
